import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SalesData } from '@org/interfaces';
import { Chart, registerables } from 'chart.js';
import { AuthService, ReportService, WebSocketService } from '@org/shared-services';
import { Observable, timer } from 'rxjs';

@Component({
    selector: 'app-eom-report',
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './eom-report.component.html',
    styleUrl: './eom-report.component.scss'
})
export class EomReportComponent {

  salesData: SalesData[] = [];
  salesForm!: FormGroup;
  salesGridForm!: FormGroup; // New FormGroup for grid
  chart: any;
  formMessage: string = '';
  // Array to store submitted sales data
  submittedSales: any[] = [];
  currentYear: number = 0;

  
 // Updated monthNames array in the component
monthNames: { value: string, display: string }[] = [
  { value: '01', display: 'January' },
  { value: '02', display: 'February' },
  { value: '03', display: 'March' },
  { value: '04', display: 'April' },
  { value: '05', display: 'May' },
  { value: '06', display: 'June' },
  { value: '07', display: 'July' },
  { value: '08', display: 'August' },
  { value: '09', display: 'September' },
  { value: '10', display: 'October' },
  { value: '11', display: 'November' },
  { value: '12', display: 'December' }
];


  salesData$!: Observable<SalesData[]>;



  constructor(
    private fb: FormBuilder,
    private reportService: ReportService,
    private ws: WebSocketService,
    private authService: AuthService
  ) {
    this.salesData$ = this.reportService.salesData$;  
  }

  ngOnInit(): void {
    Chart.register(...registerables);
    this.currentYear = new Date().getFullYear();
    if (this.chart) {
      this.chart.destroy();
      this.chart = null; // Set the chart instance to null after destroying
    }
    const currentMonth = this.getCurrentMonth();
   
    // Initialize the form with default values, including the current month
    this.salesForm = this.fb.group({
      month: [currentMonth, [Validators.required]], // Default to current month
      totalSales: [null, [Validators.required, Validators.min(0)]],
      grossProfit: [null, [Validators.required, Validators.min(0)]],
      inventory: [false, [Validators.required]],
      DNC: [null, [Validators.required, Validators.min(0)]],
    });

     // Initialize the salesGridForm
     this.salesGridForm = this.fb.group({
      salesArray: this.fb.array([]) // This is the FormArray for grid rows
    });

    this.reportService.getRolling12MonthsSales();
    this.salesData$.subscribe({
      next: (data) => {
        this.salesData = data.reverse();
        this.submittedSales = data.reverse();
      },
      error: (error) => {
        console.error('Error fetching sales data:', error);
      }
    });

    this.ws.getIo()?.on('privateMessage', (data: any) => { 
      
      if (data.type === 'update' && this.authService.isLoggedIn() ) {
        this.salesData.push(data);
        this.submittedSales.push(data);
      }
    });

  }

  ngAfterViewInit(): void {
      const ctx = document.getElementById('salesChart') as HTMLCanvasElement;
      // Check if the canvas element exists before updating the chart
      if (ctx) {
        this.salesData$.subscribe(data => {
          this.updateChart(data);
          this.populateSalesArray(data.reverse());
        });
        
      } else {
        console.warn('Canvas element with ID "salesChart" not found. Mobile View is in place');
      }
  }

   // Method to create a new form group for each row
   createGridForm(sale: SalesData, currentMonth: string = this.getCurrentMonth()): FormGroup {
    return this.fb.group({
      month: [sale.month, [Validators.required]],
      totalSales: [sale.totalSales, [Validators.required, Validators.min(0)]],
      grossProfit: [sale.grossProfit, [Validators.required, Validators.min(0)]],
      DNC: [sale.DNC, [Validators.required, Validators.min(0)]],
      inventory: [sale.inventory, [Validators.required]],
    });
   }
  
  // Method to populate the FormArray with existing sales data
  populateSalesArray(salesData: SalesData[]): void {
    const control = this.salesArray;
    control.clear(); // Clear any existing controls
    salesData.forEach((sales) => {
      const val: FormGroup = this.createGridForm(sales);
      val.disable();
      control.push(val);
    });

    
  }

  // Function to get the current month name
  getCurrentMonth(): string {
    const date = new Date();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Get 2-digit month
    const year = date.getFullYear();
    return `${month}-${year}`; // Return format 'MM-YYYY'
  }

  loadSalesData(): void { 
    if ('caches' in window) {
      caches.match('/api/').then(response => {
        if (response) {
         // console.log('Data found in cache:', response);
        } else {
         // console.log('Data not found in cache');
          this.reportService.getRolling12MonthsSales();
        }
      });
    } else { 
      this.reportService.getRolling12MonthsSales();
    }

  }

  // populateSalesArray(): void {
  //   const control = this.salesArray;
  //   control.clear(); // Clear existing entries

  //   this.salesData.forEach(sales => {
  //     control.push(this.fb.group({
  //       month: [sales.month, Validators.required],
  //       totalSales: [sales.totalSales, [Validators.required, Validators.min(0)]],
  //       grossProfit: [sales.grossProfit, [Validators.required, Validators.min(0)]],
  //       DNC: [sales.DNC, [Validators.required, Validators.min(0)]],
  //       inventory: [sales.inventory, Validators.required]
  //     }));
  //   });
  //   console.log('SALE ARRAY: ', this.salesArray.controls);
  // }

  // Submit method to handle form submission
  submitSalesData(): void {
    if (this.salesForm.valid) {
      const newSalesData = this.salesForm.value;

      //Send to backend API
      this.reportService.postSalesData(newSalesData).subscribe({
        next: (response) => {
         
          this.submittedSales.push(newSalesData);
          // Log the form data or send it to your backend API
         
          this.formMessage = 'Sales data has been successfully submitted!';
          this.salesForm.reset({
            month: this.getCurrentMonth(),
            inventory: ''
          });  // Reset the form and default month
          this.reportService.getRolling12MonthsSales();
        },
        error: (error) => {
          console.error('Error submitting sales data:', error);
        }
      });
    } else {
      // Display error if form is invalid
      this.formMessage = 'Please fill in all required fields.';
    }
  }

  get salesArray(): FormArray {
    return this.salesGridForm.get('salesArray') as FormArray;
  }
  

  // Save individual sales entry in the grid
  saveGridSalesData(index: number): void {
    const updatedSales = this.salesArray.at(index).value;
    // Send updated data to backend
    this.reportService.updateSalesData(updatedSales).subscribe(
      response => console.log('Sales data updated'),
      error => console.error('Error updating sales data', error)
    );
  }

  // Delete individual sales entry in the grid
  deleteGridSalesData(index: number): void {
    const salesId = this.salesData[index]._id; // Assuming salesData has an _id field

    this.reportService.deleteSalesData(salesId).subscribe(
      response => {
        
        this.salesArray.removeAt(index); // Remove the entry from the form array
      },
      error => console.error('Error deleting sales data', error)
    );
  }

  // // Initialize Chart.js chart
  // initializeChart(): void {
  //   const ctx = document.getElementById('salesChart') as HTMLCanvasElement;
  //   new Chart(ctx, {
  //     type: 'bar', // Example chart type
  //     data: {
  //       labels: ['January', 'February', 'March', 'April'], // Example labels
  //       datasets: [
  //         {
  //           label: 'Total Sales',
  //           data: [3000, 4000, 5000, 2000], // Example data
  //           backgroundColor: 'rgba(54, 162, 235, 0.2)',
  //           borderColor: 'rgba(54, 162, 235, 1)',
  //           borderWidth: 1
  //         }
  //       ]
  //     },
  //     options: {
  //       responsive: true,
  //       scales: {
  //         y: {
  //           beginAtZero: true
  //         }
  //       }
  //     }
  //   });
  // }

   // Method to update the chart with fetched data
   updateChart(salesData: any[]): void {
    const months = salesData.map(item => item.month); // Extract months
    const totalSales = salesData.map(item => item.totalSales); // Extract total sales

    const ctx = document.getElementById('salesChart') as HTMLCanvasElement;

  // Destroy the existing chart instance if it exists
  if (this.chart) {
    this.chart.destroy();
    this.chart = null; // Set the chart instance to null after destroying
  }
     
   

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: months, // Display months as labels
        datasets:  this.desktopChartDataset(salesData)
      },
      options: this.desktopChartOptions()
    });
   }
  
   isMobileDevice(): boolean {
    return /Mobi|Android/i.test(navigator.userAgent);
   }
  
  mobileChartDataset(salesData: SalesData[]): any { 
    return  [
      {
        label: 'Total Sales',
        data: salesData.map(data => data.totalSales),
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }
    ]
  }

  mobileChartOptions(): any { 
    return {
      responsive: true,
      animation: false,  // Disable animations
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Month'
          }
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Sales'
          }
        }
      },
      plugins: {
        tooltip: {
          enabled: false // Disable tooltips for better performance
        },
        legend: {
          display: false // Hide the legend
        }
      }
    };
  }
  
  desktopChartDataset(salesData: SalesData[]): any {
    return  [
      {
        label: 'Closed Sales',
        data: salesData.map((data: { totalSales: number; DNC: number; }) => data.totalSales - data.DNC), // Closed sales = Total sales - DNC
        backgroundColor: 'rgba(54, 162, 235, 0.2)',  // Light blue for closed sales
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'Delivered Not Closed (DNC)',
        data: salesData.map(data => data.DNC), // DNC data
        backgroundColor: 'rgba(255, 99, 132, 0.2)', // Light red for DNC
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      },
      {
        label: 'Gross Profit',
        data: salesData.map(data => data.grossProfit),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
        type: 'line', // Represent Gross Profit as a line
        yAxisID: 'y-axis-2' // Separate Y-axis for profit
      },
      {
        label: 'Inventory Done (I.D.)',
        data: salesData.map(data => data.inventory ? 1 : 0), // Inventory Done as boolean (1 or 0)
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1,
        type: 'line',
        yAxisID: 'y-axis-2' // Boolean data on secondary axis
      }
    ]
  }

  desktopChartOptions(): any { 
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          stacked: true, // Enable stacking for Closed Sales and DNC
          title: {
            display: true,
            text: 'Sales Amount ($)'
          }
        },
        'y-axis-2': {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          title: {
            display: true,
            text: 'Gross Profit & Inventory Done'
          },
          ticks: {
            callback: function(value: number) {
              return value === 1 ? 'Done' : ''; // Custom labels for I.D.
            }
          }
        }
      }
    }
  }
  

  




  // Clean up the chart instance when the component is destroyed
  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy(); // Destroy the chart if it exists
      this.chart = null; // Reset the chart instance to null
    }
  }
  
  

}
